import { Link } from '@tanstack/react-router';
import type { AdminCaseSummary } from '@casetrace/shared';
import { StatusBadge } from '../../components/ui/badge';
import { formatDateTime, formatSourceType } from './admin-format';
import { AdminCaseEditor } from './admin-case-editor';

interface AdminCaseCardProps {
  summary: AdminCaseSummary;
}

export function AdminCaseCard({ summary }: AdminCaseCardProps) {
  return (
    <article className="rounded-lg border border-white/[0.07] bg-white/3 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/cases/$caseId"
            params={{ caseId: summary.case.id }}
            className="text-sm font-medium text-text-primary hover:text-white"
          >
            {summary.case.navn}
          </Link>
          <p className="mt-0.5 text-xs text-text-tertiary">
            {summary.case.saksnummer} · {summary.case.klient}
          </p>
        </div>
        <StatusBadge status={summary.case.status} />
      </div>

      <div className="mt-4 grid gap-2 text-xs text-text-secondary sm:grid-cols-3">
        <span>{summary.sources.length} kilder</span>
        <span>{summary.totalEvents} hendelser</span>
        <span>{summary.totalSourceItems} kildeelementer</span>
      </div>

      <div className="mt-4 space-y-2">
        {summary.sources.length === 0 ? (
          <p className="text-xs text-text-tertiary">Ingen opplastede kilder.</p>
        ) : (
          summary.sources.map((sourceSummary) => (
            <div
              key={sourceSummary.source.id}
              className="rounded-md border border-white/6 bg-white/3 px-3 py-2"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-medium text-text-primary">
                  {sourceSummary.source.navn}
                </span>
                <span className="text-[11px] text-text-tertiary">
                  {formatSourceType(sourceSummary.source.type)} · {sourceSummary.source.status}
                </span>
              </div>
              <p className="mt-1 text-[11px] text-text-tertiary">
                {sourceSummary.eventCount} hendelser · {sourceSummary.sourceItemCount}{' '}
                kildeelementer · {sourceSummary.importJobCount} importjobber
              </p>
            </div>
          ))
        )}
      </div>

      <p className="mt-4 text-[11px] text-text-tertiary">
        Oppdatert {formatDateTime(summary.case.updatedAt)}
      </p>
      <AdminCaseEditor summary={summary} />
    </article>
  );
}
